import { ApolloError } from "@apollo/client";
import {
  Box,
  Divider,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useMemo, useState } from "react";

import StyledDialog from "../../../common/components/dialog/StyledDialog";
import DeviceTemplateNodeSelect from "../../../common/components/layout/components/DeviceTemplateNodeSelect";
import SKUSelect from "../../../common/components/layout/components/SKUSelect";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useGetCustomer } from "../../../common/hooks/useGetCustomer";
import { skuCatalog } from "../../../common/models/enums";
import { checkDuplicateDeviceNames } from "../../../common/utils/checkDuplicateDeviceNames";
import { mapTemplateDataToDeviceInput } from "../../../common/utils/mapTemplateDataToDeviceInput";
import { mapTemplateDataToServiceInput } from "../../../common/utils/mapTemplateDataToServiceInput";
import { validateCreateDevicesFromTemplate } from "../../../common/utils/validateCreateDevicesFromTemplate";
import { DEFAULT_DEVICE_PASSWORD } from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocation } from "../../customer-settings/tabs/locations/hooks/useGetDevicesByLocation";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useGetNodes } from "../../nodes/hooks/useGetNodes";
import { useCreateBatchDevices } from "../hooks/useCreateBatchDevices";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";

// Combined representation of a component at onboarding time:
// hardware definition comes from skuCatalog, models from skuDataConfigurations
type OnboardingComponent = {
  name: string; // from catalog (e.g. "Agx 1", "Ch4", "Optical 1")
  type: string; // from catalog (e.g. "AGX", "CH4", "OPT")
  makeModelId: string; // from catalog (e.g. "CC_AGX", "CC_CH4")
  isPT: boolean; // from catalog — fixed, no toggle needed
  models: string[]; // from configuration's skuDataConfigurations
};

interface SelectedSKU {
  id: string;
  skuNumber: string;
  configurationName: string;
  category: string;
  skuDataConfigurations: string;
  nodeCount: number;
  defaultIp?: string | null;
}

interface CreateDevicesFromSKUDialogProps {
  open: boolean;
  handleClose: () => void;
}

const CreateDevicesFromSKUDialog = ({
  open,
  handleClose,
}: CreateDevicesFromSKUDialogProps) => {
  const theme = useTheme();
  const confirm = useConfirm();
  const selectedCustomerId = useCustomerIdGuard();
  const selectedLocation = useSelectedLocationDropdown();
  const { data } = useGetNodes({
    customerId: selectedCustomerId,
    includeDevices: true,
  });
  const { data: currentDeviceData, refetchDevices } = useGetDevicesByLocation(
    selectedLocation?.value ?? ""
  );
  const { customer } = useGetCustomer();
  const { syncShadowsForNode } = useSyncShadow();
  const { batchCreateDevices, loading } = useCreateBatchDevices();
  const { batchCreateServices } = useCreateBatchServices();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [devicePassword, setDevicePassword] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [selectedSKU, setSelectedSKU] = useState<SelectedSKU | null>(null);

  // Component list built from catalog + configuration models
  const [components, setComponents] = useState<OnboardingComponent[]>([]);

  const [selectedNodes, setSelectedNodes] = useState<
    { nodeId: string; nodeIP: string; nodeName: string }[]
  >([]);

  const [componentSerialNumbers, setComponentSerialNumbers] = useState<
    string[]
  >([]);
  const [deviceIpAddresses, setDeviceIpAddresses] = useState<string[]>([]);

  // PT IPs — only relevant for components where isPT === true
  const [devicePTIPs, setDevicePTIPs] = useState<string[]>([]);
  const [devicePTTypes, setDevicePTTypes] = useState<string[]>([]);

  useEffect(() => {
    if (!devicePassword) {
      setDevicePassword(customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer?.devicePassword]);

  const resetDialogState = () => {
    setHasSubmitted(false);

    setDevicePassword("");

    setSerialNumber("");

    setSelectedSKU(null);

    setComponents([]);

    setSelectedNodes([]);

    setComponentSerialNumbers([]);

    setDeviceIpAddresses([]);

    setDevicePTIPs([]);

    setDevicePTTypes([]);
  };

  const closeDialog = (): void => {
    handleClose();

    resetDialogState();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" — keep open
      });
  };

  const existingNames =
    currentDeviceData?.getDevicesByLocation?.items
      ?.map(d => d?.id?.replace(/^DE#/, "").toLowerCase())
      .filter((id): id is string => !!id) ?? [];

  const filteredNodes = useMemo(() => {
    if (!data) return [];

    return data.getNodes?.items
      ?.map(node => ({
        nodeId:
          node?.nodeId
            ?.replace("C_", "C#")
            .replace("_L_", "#L#")
            .replace("_N_", "#N#") ?? "",
        nodeIP: node?.nodeIP ?? "",
        nodeName: node?.nodeName ?? "",
      }))
      .filter(n => n.nodeId && n.nodeIP && n.nodeName);
  }, [data]);

  const handleSKUChange = ({
    id,
    skuNumber,
    configurationName,
    category,
    skuDataConfigurations,
    nodeCount,
    defaultIp,
  }: {
    id: string;
    skuNumber: string;
    configurationName: string;
    category: string;
    skuDataConfigurations: string;
    nodeCount: number;
    defaultIp?: string | null;
  }) => {
    try {
      // Get fixed hardware structure from catalog (Tier 1)
      const catalogEntry = skuCatalog[skuNumber];

      if (!catalogEntry) {
        errorNotification(`Unknown SKU number: ${skuNumber}`);

        return;
      }

      // Parse model assignments from configuration (Tier 2)
      const configData: { name: string; models: string[] }[] = JSON.parse(
        skuDataConfigurations
      );

      // Build combined component list — skip AGX/POE (nodes and infrastructure, not devices)
      const merged: OnboardingComponent[] = catalogEntry.subComponents
        .filter(sub => sub.type !== "AGX" && sub.type !== "POE")
        .map(sub => {
          const configEntry = configData.find(c => c.name === sub.name);

          return {
            name: sub.name,
            type: sub.type,
            makeModelId: sub.makeModelId,
            isPT: sub.isPT,
            models: configEntry?.models ?? [],
          };
        });

      setSelectedSKU({
        id,
        skuNumber,
        configurationName,
        category,
        skuDataConfigurations,
        nodeCount,
      });

      setComponents(merged);

      const prefillIp = (defaultIp ?? "").replaceAll(" ", "");

      setDeviceIpAddresses(Array(merged.length).fill(prefillIp));

      setComponentSerialNumbers(Array(merged.length).fill(""));

      setDevicePTIPs(Array(merged.length).fill(""));

      setDevicePTTypes(Array(merged.length).fill(""));

      setSelectedNodes(
        Array(merged.length).fill({ nodeId: "", nodeIP: "", nodeName: "" })
      );

      refetchDevices(selectedLocation?.value ?? "");

      setHasSubmitted(false);
    } catch {
      errorNotification("Invalid SKU Configuration data format");
    }
  };

  // Auto-determine PT type from IP comparison
  const determinePTType = (deviceIP: string, ptIP: string): string => {
    if (!ptIP || ptIP === deviceIP) return "CH4-UCv2";

    return "JSys";
  };

  const handleDeviceIpChange = (index: number, value: string) => {
    const cleaned = value.replaceAll(" ", "");

    setDeviceIpAddresses(prev => {
      const updated = [...prev];

      updated[index] = cleaned;

      return updated;
    });

    // Re-evaluate PT type if PT IP is already set
    const currentPTIP = devicePTIPs[index];

    if (components[index]?.isPT && currentPTIP) {
      setDevicePTTypes(prev => {
        const updated = [...prev];

        updated[index] = determinePTType(cleaned, currentPTIP);

        return updated;
      });
    }
  };

  const handlePTIPChange = (index: number, ptIP: string) => {
    const cleaned = ptIP.replaceAll(" ", "");

    setDevicePTIPs(prev => {
      const updated = [...prev];

      updated[index] = cleaned;

      return updated;
    });

    const deviceIP = deviceIpAddresses[index] || "";

    setDevicePTTypes(prev => {
      const updated = [...prev];

      updated[index] = determinePTType(deviceIP, cleaned);

      return updated;
    });
  };

  const handleNodeSelect = (
    index: number,
    node: { nodeId: string; nodeIP: string; nodeName: string }
  ) => {
    const updated = [...selectedNodes];

    updated[index] = node;

    setSelectedNodes(updated);
  };

  const handleComponentSerialChange = (index: number, value: string) => {
    setComponentSerialNumbers(prev => {
      const updated = [...prev];

      updated[index] = value;

      return updated;
    });
  };

  // Limit node selection to nodeCount unique nodes
  const getFilteredNodesForDevice = (deviceIndex: number) => {
    const uniqueSelectedIds = new Set(
      selectedNodes.filter(n => n.nodeId).map(n => n.nodeId)
    );
    const maxNodes = selectedSKU?.nodeCount ?? 1;
    const currentDeviceNodeId = selectedNodes[deviceIndex]?.nodeId;

    return filteredNodes.filter(node => {
      if (node.nodeId === currentDeviceNodeId) return true;

      if (uniqueSelectedIds.has(node.nodeId)) return true;

      return uniqueSelectedIds.size < maxNodes;
    });
  };

  const saveDevice = async (): Promise<void> => {
    setHasSubmitted(true);

    if (!selectedSKU || components.length === 0) {
      errorNotification("Please select a SKU configuration");

      return;
    }

    // Validate IPs
    const missingIps = deviceIpAddresses.some(ip => !ip.trim());

    if (missingIps) {
      errorNotification("All device IP addresses are required");

      return;
    }

    // Validate nodes
    const missingNodes = selectedNodes.some(n => !n.nodeId);

    if (missingNodes) {
      errorNotification("All devices must be assigned to a node");

      return;
    }

    // Validate PT IPs for PT-enabled components
    for (let i = 0; i < components.length; i++) {
      if (components[i].isPT && !devicePTIPs[i]?.trim()) {
        errorNotification(`${components[i].name}: Pan Tilt IP is required`);

        return;
      }
    }

    if (devicePassword.length > 0 && devicePassword.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    // Build template data for mapping utilities
    const templateData = components.map((comp, index) => ({
      name: comp.name,
      makeModelId: comp.makeModelId,
      deviceIp: deviceIpAddresses[index] || "",
      models: comp.models,
      isPT: comp.isPT,
      panTiltIP: comp.isPT
        ? devicePTIPs[index] || deviceIpAddresses[index]
        : undefined,
      panTiltType: comp.isPT
        ? devicePTTypes[index] ||
          determinePTType(deviceIpAddresses[index], devicePTIPs[index])
        : undefined,
      hasRecordVideo: true,
    }));

    // Validate using existing utility (checks parsedTemplateData, nodes, password)
    const validation = validateCreateDevicesFromTemplate({
      parsedTemplateData: templateData,
      selectedNodes,
      devicePassword,
    });

    if (!validation.isValid) {
      errorNotification(validation.errorMessage!);

      return;
    }

    try {
      const devicesToCreate = mapTemplateDataToDeviceInput({
        templateData,
        selectedCustomerId,
        selectedLocation: selectedLocation ?? { title: "", value: "" },
        selectedNodes,
        devicePassword,
        skuNumber: selectedSKU.skuNumber,
        configurationName: selectedSKU.configurationName,
        serialNumber: serialNumber || undefined,
        componentSerialNumbers,
      });

      const uniqueDevices = checkDuplicateDeviceNames(
        existingNames,
        devicesToCreate
      );

      const servicesToCreate = mapTemplateDataToServiceInput(
        uniqueDevices,
        templateData
      );

      await batchCreateDevices(uniqueDevices);

      const serviceResponse = await batchCreateServices(servicesToCreate);

      if (serviceResponse?.errors) {
        errorNotification("Failed to create services");

        return;
      }

      closeDialog();

      successNotification("Devices created from SKU Configuration!");

      try {
        await Promise.all(
          selectedNodes.map(async node => {
            if (node?.nodeId) {
              await syncShadowsForNode(node.nodeId);
            }
          })
        );
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("Device ID already exists");
      } else {
        errorNotification((error as ApolloError).message);
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      title="Create new device(s) from SKU configuration"
      onClose={confirmDialogClose}
      SubmitButtonProps={{
        loading,
        onSubmit: saveDevice,
      }}
    >
      <Grid container justifyContent="center" direction="column" spacing={2}>
        <Grid item>
          <Typography variant="body1">Location</Typography>
          <Box sx={{ color: theme.palette.text.primary, fontWeight: "bold" }}>
            {selectedLocation?.title}
          </Box>
        </Grid>

        <Grid item>
          <SKUSelect
            skuId={selectedSKU?.skuNumber ?? ""}
            onSKUChange={handleSKUChange}
            error={hasSubmitted && !selectedSKU?.skuNumber}
            helperText={
              hasSubmitted && !selectedSKU?.skuNumber
                ? "SKU selection is required"
                : ""
            }
          />
        </Grid>

        <Grid item>
          <TextField
            fullWidth
            label="Unit Serial Number (S/N)"
            value={serialNumber}
            onChange={e => setSerialNumber(e.target.value)}
            helperText="Serial number for this physical unit (optional)"
          />
        </Grid>

        <Grid item>
          <TextField
            fullWidth
            label="Device Password"
            value={devicePassword}
            onChange={e => setDevicePassword(e.target.value)}
            placeholder="Camera credentials (optional)"
            error={devicePassword.length > 0 && devicePassword.length < 8}
            helperText={
              devicePassword.length > 0 && devicePassword.length < 8
                ? "Password must be at least 8 characters"
                : "Can be edited per device after onboarding"
            }
          />
        </Grid>

        {selectedSKU && components.length > 0 && (
          <Grid item>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Assign Components to Nodes
            </Typography>
            <Box sx={{ width: "100%" }}>
              {components.map((comp, index) => (
                <Box
                  key={comp.name + index}
                  sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* Component header */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {comp.name}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        fontWeight: 600,
                      }}
                    >
                      {comp.type}
                    </Typography>
                    {comp.models.length > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {comp.models.join(", ")}
                      </Typography>
                    )}
                  </Typography>
                  <Divider
                    sx={{
                      mb: 1.5,
                      mt: 0.5,
                      borderBottomWidth: 2,
                      borderColor: "primary.main",
                    }}
                  />

                  {/* IP Address */}
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="Device IP"
                      value={deviceIpAddresses[index] || ""}
                      onChange={e =>
                        handleDeviceIpChange(index, e.target.value)
                      }
                      error={hasSubmitted && !deviceIpAddresses[index]?.trim()}
                      helperText={
                        hasSubmitted && !deviceIpAddresses[index]?.trim()
                          ? "IP address is required"
                          : ""
                      }
                      placeholder="192.168.1.x"
                      fullWidth
                    />
                  </Box>

                  {/* Node assignment */}
                  <Box sx={{ mb: 1 }}>
                    <DeviceTemplateNodeSelect
                      selectedNodeId={selectedNodes[index]?.nodeId ?? ""}
                      filteredNodes={getFilteredNodesForDevice(index)}
                      onChange={node => handleNodeSelect(index, node)}
                      error={hasSubmitted && !selectedNodes[index]?.nodeId}
                      helperText={
                        hasSubmitted && !selectedNodes[index]?.nodeId
                          ? "Node is required"
                          : ""
                      }
                      size="small"
                      fullWidth
                    />
                  </Box>

                  {/* Component serial number */}
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="Component S/N"
                      value={componentSerialNumbers[index] || ""}
                      onChange={e =>
                        handleComponentSerialChange(index, e.target.value)
                      }
                      placeholder="Optional"
                      fullWidth
                    />
                  </Box>

                  {/* Pan Tilt IP — shown only for components with isPT from catalog */}
                  {comp.isPT && (
                    <Box sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        label="Pan Tilt IP"
                        value={devicePTIPs[index] || ""}
                        onChange={e => handlePTIPChange(index, e.target.value)}
                        placeholder="192.168.1.x (same as device IP if integrated)"
                        error={hasSubmitted && !devicePTIPs[index]?.trim()}
                        helperText={
                          hasSubmitted && !devicePTIPs[index]?.trim()
                            ? "Pan Tilt IP is required"
                            : devicePTTypes[index]
                            ? `Type: ${devicePTTypes[index]}`
                            : "Enter to auto-detect PT type"
                        }
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </StyledDialog>
  );
};

export default CreateDevicesFromSKUDialog;
